import type React from 'react'
import { createContext, useContext } from 'react'
import type { IOrganization, IOrganizationRef, IUser } from '../../../Model/index.js'
import { OrganizationType } from '../../../Model/index.js'
import { Environment } from '../../../environment.js'
import { tracingAxios } from '../../../Repository/Sentry.js'
import { useGetAuthenticatedUser, useGetOrganizationsOfOrderSelector } from '../../../Flux/Selector/index.js'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import { useGetUsersSelector } from '../../../Flux/Selector/Order/GetUsers/GetUsersSelector.js'
import { user1 } from '../../../Util/stubs/testUsers.stub.ts'

type OrganizationContextType = {
  getOrganization: (id: string) => Promise<IOrganizationRef>
  getUser: (userId: string) => Promise<IUser>
}

const testOrga: IOrganization = {
  id: 'Genomics Lab A',
  name: 'Genomics Lab A',
  type: OrganizationType.LABORATORY,
  isPublic: false,
  defaultSampleConfigurationType: 'SINGLE_SAMPLE_PAIRS',
  isBillingMissing: false,
  defaultRedlistMode: 'DE',
  defaultProcessingType: 'METABARCODING'
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

const inProgressRequests: Record<string, Promise<IOrganizationRef>> = {}
const inProgressRequestsUsers: Record<string, Promise<IUser>> = {}

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const organizations = useGetOrganizationsOfOrderSelector()
  const users = useGetUsersSelector()
  const authUser = useGetAuthenticatedUser()

  const dispatch = useDispatch()

  const getOrganization = async (id: string): Promise<IOrganizationRef> => {
    if (organizations[id]) {
      // Return cached result
      return organizations[id]!
    }

    if (inProgressRequests && !!inProgressRequests[id]) {
      // If request is already in progress, wait for its completion
      return inProgressRequests[id]
    }

    if (Environment.DEV_SKIP_LOGIN === 'true') {
      return Promise.resolve(testOrga)
    }

    // Create a new request and store it in progress
    const request = tracingAxios
      .get<IOrganization>(`${Environment.API_URL}/api/organizations/${id}`, { withCredentials: true })
      .then((response) => {
        const organization = response.data
        dispatch(Actions[orderSlice.name].setOrganization({ id: organization.id, organization: organization }))
        delete inProgressRequests[id]
        return organization
      })

    inProgressRequests[id] = request
    return request
  }

  const getUser = async (userId: string): Promise<IUser> => {
    if (users[userId]) {
      // Return cached result
      return users[userId]!
    }

    if (!!authUser && authUser.id == userId) {
      return authUser
    }

    if (inProgressRequestsUsers && !!inProgressRequestsUsers[userId]) {
      // If request is already in progress, wait for its completion
      return inProgressRequestsUsers[userId]
    }

    if (Environment.DEV_SKIP_LOGIN === 'true') {
      return Promise.resolve(user1)
    }

    // Create a new request and store it in progress
    const request = tracingAxios.get<IUser>(`${Environment.API_URL}/api/users/${userId}`, { withCredentials: true }).then((response) => {
      const user = response.data
      dispatch(Actions[orderSlice.name].setUser({ id: user.id, user: user }))
      delete inProgressRequestsUsers[userId]
      return user
    })

    inProgressRequestsUsers[userId] = request
    return request
  }

  return <OrganizationContext.Provider value={{ getOrganization, getUser }}>{children}</OrganizationContext.Provider>
}
